// Which audio track should play by default — decided from TMDB, not guessed.
//
// The old heuristic was a fixed list (Malayalam → English → Tamil), which is
// wrong the moment the movie is, say, a Telugu release: the release carries
// Tamil/Telugu/Hindi/Malayalam/Kannada, and the track that matches the film's
// own language is the one a viewer expects. TMDB knows that language, so it
// drives the choice and the fixed list becomes a fallback.
//
// The matching has to bridge two vocabularies: TMDB speaks ISO 639-1 ("te"),
// while an MKV tag speaks ISO 639-2/B ("tel") and the human label speaks English
// ("Telugu"). Releases are inconsistent about which of the three they set, so
// all three are matched, and a combined label like "Tamil, Telugu" still works.
export const TMDB_LANGUAGE = {
  ml: { name: "Malayalam", iso3: ["mal"] },
  ta: { name: "Tamil", iso3: ["tam"] },
  te: { name: "Telugu", iso3: ["tel"] },
  hi: { name: "Hindi", iso3: ["hin"] },
  kn: { name: "Kannada", iso3: ["kan"] },
  bn: { name: "Bengali", iso3: ["ben"] },
  mr: { name: "Marathi", iso3: ["mar"] },
  pa: { name: "Punjabi", iso3: ["pan"] },
  gu: { name: "Gujarati", iso3: ["guj"] },
  or: { name: "Oriya", iso3: ["ori"] },
  as: { name: "Assamese", iso3: ["asm"] },
  ur: { name: "Urdu", iso3: ["urd"] },
  si: { name: "Sinhala", iso3: ["sin"] },
  ne: { name: "Nepali", iso3: ["nep"] },
  en: { name: "English", iso3: ["eng"] },
  fr: { name: "French", iso3: ["fre", "fra"] },
  es: { name: "Spanish", iso3: ["spa"] },
  de: { name: "German", iso3: ["ger", "deu"] },
  it: { name: "Italian", iso3: ["ita"] },
  pt: { name: "Portuguese", iso3: ["por"] },
  ru: { name: "Russian", iso3: ["rus"] },
  ar: { name: "Arabic", iso3: ["ara"] },
  zh: { name: "Chinese", iso3: ["chi", "zho"] },
  ja: { name: "Japanese", iso3: ["jpn"] },
  ko: { name: "Korean", iso3: ["kor"] },
  th: { name: "Thai", iso3: ["tha"] },
  id: { name: "Indonesian", iso3: ["ind"] },
  tr: { name: "Turkish", iso3: ["tur"] },
  nl: { name: "Dutch", iso3: ["dut", "nld"] },
  pl: { name: "Polish", iso3: ["pol"] },
  sv: { name: "Swedish", iso3: ["swe"] },
  da: { name: "Danish", iso3: ["dan"] },
  no: { name: "Norwegian", iso3: ["nor"] },
  fi: { name: "Finnish", iso3: ["fin"] },
  cs: { name: "Czech", iso3: ["cze", "ces"] },
  el: { name: "Greek", iso3: ["gre", "ell"] },
  he: { name: "Hebrew", iso3: ["heb"] },
  fa: { name: "Persian", iso3: ["per", "fas"] },
};

/** English name for a TMDB (639-1) code, or the code itself when unknown. */
export function languageName(code) {
  const key = String(code || "").toLowerCase();
  return TMDB_LANGUAGE[key]?.name || key || "";
}

/** Does this track look like it is in `code`? Accepts 639-1, 639-2/B and names. */
export function trackMatchesLanguage(track, code) {
  const key = String(code || "").toLowerCase();
  if (!key) return false;
  const def = TMDB_LANGUAGE[key];
  const code2 = String(track.code || "").toLowerCase();
  const label = String(track.label || track.name || "").toLowerCase();
  const variants = new Set([key, ...(def?.iso3 || [])]);
  if (code2 && variants.has(code2)) return true;
  // A track may carry a 3-letter code we know even when the TMDB code is 2-letter.
  for (const [k, v] of Object.entries(TMDB_LANGUAGE)) {
    if (v.iso3.includes(code2) && (k === key || v.iso3.includes(key))) return true;
  }
  if (def && label.includes(def.name.toLowerCase())) return true;
  if (label.includes(key)) return true; // "Tamil, Telugu" / "te"
  return false;
}

/**
 * Pick the default audio track.
 *
 * Order: TMDB's own languages first (original language, then the rest in TMDB's
 * order), then the operator's preference list, then the first track. Every answer
 * carries `reason` so the UI can say *why* instead of silently choosing.
 */
export function guessDefaultAudio(tracks, { tmdbLanguages = [], preferred = ["malayalam", "english", "tamil"] } = {}) {
  const list = Array.isArray(tracks) ? tracks.filter(Boolean) : [];
  if (!list.length) return null;
  for (const code of tmdbLanguages) {
    if (!code) continue;
    const hit = list.find((t) => trackMatchesLanguage(t, code));
    if (hit) {
      return { index: hit.index, label: hit.label, code: hit.code || null, reason: `matches TMDB language (${languageName(code) || code})`, source: "tmdb" };
    }
  }
  for (const want of preferred) {
    const hit = list.find((t) => String(t.label || "").toLowerCase().includes(String(want).toLowerCase()));
    if (hit) {
      return { index: hit.index, label: hit.label, code: hit.code || null, reason: `preferred language (${want})`, source: "preference" };
    }
  }
  const first = list[0];
  return { index: first.index, label: first.label, code: first.code || null, reason: "first track in the release", source: "first" };
}

/** The subtitle languages worth keeping from a release (labels as ffprobe sees them). */
export function pickSubtitleLanguages(subtitles, wanted = ["English"]) {
  const list = Array.isArray(subtitles) ? subtitles : [];
  const out = [];
  for (const w of wanted) {
    const hit = list.find((s) => trackMatchesLanguage(s, String(w).toLowerCase().slice(0, 2)) || String(s.label || "").toLowerCase().includes(String(w).toLowerCase()));
    if (hit && !out.includes(hit.label)) out.push(hit.label);
  }
  return out;
}
